/**
 * Open Stripe Checkout and land back on the hub receipt.
 *
 * Web: navigate to Checkout; Stripe's success_url is `/?guest_paid=1…`.
 * Native: in-app browser (SFSafariViewController). success_url is an HTTPS
 * bounce that hands control back via `com.ziawellnav.app://guest-paid`. The
 * hub stays mounted and shows ServicePaidReceipt only after paid.
 */
import { isIosNativeAppRuntime, openExternalUrl } from './runtimeCapabilities';
import {
  dispatchGuestPaidCancel,
  dispatchGuestPaidReturn,
  isGuestPaidCancelHref,
  parseGuestPaidHref,
  persistGuestPaidSession,
  subscribeGuestPaidCancel,
  subscribeGuestPaidReturn,
  type GuestPaidReturn,
} from './guestPaidReturn';

export type OpenServiceCheckoutResult =
  | { outcome: 'paid'; paid: GuestPaidReturn }
  | { outcome: 'cancelled' };

export type OpenServiceCheckoutHint = {
  companyFunded?: boolean;
  service?: string;
  requestId?: string | null;
  email?: string | null;
};

function mergePaid(hint: OpenServiceCheckoutHint, hit?: GuestPaidReturn | null): GuestPaidReturn {
  return {
    service: hit?.service || hint.service || '',
    requestId: hit?.requestId || hint.requestId || null,
    email: (hit?.email || hint.email || '').trim().toLowerCase() || null,
  };
}

async function closeNativeBrowser(): Promise<void> {
  try {
    const { Browser } = await import('@capacitor/browser');
    if (typeof Browser?.close === 'function') await Browser.close();
  } catch {
    /* already closed, or plugin not synced yet */
  }
}

async function openNativeCheckoutWindow(checkoutUrl: string): Promise<'browser' | 'external'> {
  try {
    const { Browser } = await import('@capacitor/browser');
    if (typeof Browser?.open === 'function') {
      await Browser.open({ url: checkoutUrl, presentationStyle: 'fullscreen' });
      return 'browser';
    }
  } catch {
    /* plugin missing until the next iOS cap sync */
  }
  const opened = openExternalUrl(checkoutUrl, { tryHostBridge: false });
  if (!opened.ok) window.location.href = checkoutUrl;
  return 'external';
}

async function receiptLooksPaid(requestId: string, email: string): Promise<boolean> {
  try {
    const res = await fetch(
      `/api/service-requests/receipt?requestId=${encodeURIComponent(requestId)}&email=${encodeURIComponent(email)}`,
      { headers: { Accept: 'application/json' } },
    );
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; receipt?: { paid?: boolean } };
    return Boolean(data?.success && data?.receipt?.paid);
  } catch {
    return false;
  }
}

function handleNativeReturnUrl(url: string): boolean {
  const hit = parseGuestPaidHref(url);
  if (hit) {
    persistGuestPaidSession(hit);
    dispatchGuestPaidReturn(hit);
    void closeNativeBrowser();
    return true;
  }
  if (isGuestPaidCancelHref(url)) {
    dispatchGuestPaidCancel();
    void closeNativeBrowser();
    return true;
  }
  return false;
}

let nativeReturnInstalled = false;

/** Cold-start + background returns from the Stripe bounce / custom scheme. */
export function installGuestPaidNativeReturn(): void {
  if (nativeReturnInstalled || typeof window === 'undefined') return;
  nativeReturnInstalled = true;
  if (!isIosNativeAppRuntime()) return;
  void (async () => {
    try {
      const { App } = await import('@capacitor/app');
      await App.addListener('appUrlOpen', (event) => {
        handleNativeReturnUrl(String(event?.url || ''));
      });
      const launch = await App.getLaunchUrl();
      if (launch?.url) handleNativeReturnUrl(launch.url);
    } catch {
      /* @capacitor/app lands on the next iOS cap sync */
    }
  })();
}

/**
 * Open Stripe Checkout. Web leaves the page. Native stays in the hub and
 * resolves `paid` only after Stripe confirms (URL bounce or receipt poll).
 */
export async function openServiceCheckout(
  checkoutUrl: string,
  hint: OpenServiceCheckoutHint = {},
): Promise<OpenServiceCheckoutResult> {
  if(hint.companyFunded===true) {
    if(!hint.requestId)throw new Error('Funded booking has no receipt reference.');
    const paid=mergePaid(hint);persistGuestPaidSession(paid);return {outcome:'paid',paid};
  }
  const url = String(checkoutUrl || '').trim();
  if (!url) throw new Error('Could not open secure checkout');

  if (!isIosNativeAppRuntime()) {
    window.location.href = url;
    return new Promise<OpenServiceCheckoutResult>(() => {});
  }

  installGuestPaidNativeReturn();

  return new Promise<OpenServiceCheckoutResult>((resolve) => {
    let settled = false;
    let pollTimer: number | null = null;
    let finishedHandle: { remove: () => Promise<void> } | null = null;
    let openedInAppBrowser = false;

    const cleanup = () => {
      unsubPaid();
      unsubCancel();
      if (pollTimer != null) window.clearInterval(pollTimer);
      void finishedHandle?.remove();
    };

    const finish = (next: OpenServiceCheckoutResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      void closeNativeBrowser();
      if (next.outcome === 'paid') {
        persistGuestPaidSession(next.paid);
        dispatchGuestPaidReturn(next.paid);
      }
      resolve(next);
    };

    const unsubPaid = subscribeGuestPaidReturn((hit) => {
      finish({ outcome: 'paid', paid: mergePaid(hint, hit) });
    });
    const unsubCancel = subscribeGuestPaidCancel(() => finish({ outcome: 'cancelled' }));

    const requestId = String(hint.requestId || '').trim();
    const email = String(hint.email || '').trim();
    if (requestId && email) {
      const tick = () => {
        void receiptLooksPaid(requestId, email).then((paid) => {
          if (paid) finish({ outcome: 'paid', paid: mergePaid(hint) });
        });
      };
      pollTimer = window.setInterval(tick, 2000);
      window.setTimeout(tick, 1600);
    }

    void (async () => {
      try {
        const { Browser } = await import('@capacitor/browser');
        if (typeof Browser?.addListener === 'function') {
          finishedHandle = await Browser.addListener('browserFinished', () => {
            window.setTimeout(() => {
              if (openedInAppBrowser && !settled) finish({ outcome: 'cancelled' });
            }, 400);
          });
        }
      } catch {
        /* plugin missing */
      }
      try {
        const mode = await openNativeCheckoutWindow(url);
        openedInAppBrowser = mode === 'browser';
      } catch {
        window.location.href = url;
      }
    })();
  });
}
